import { Component, OnInit } from '@angular/core';
import { AnalystRoleEnum } from '@compumark/analyst-selection-tool-client';
import { CustomTextFilterComponent } from '@compumark/brand-context-components';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GridApi, GridOptions } from 'ag-grid-community';
import { debounceTime, fromEvent } from 'rxjs';
import { MaterialTooltipComponent } from 'src/app/data-grid/components/material-tooltip/material-tooltip.component';
import { FilterParams } from 'src/app/results/services/filters.params';
import { UserService } from 'src/app/security/services/user.service';
import { OrderHistoryDetailsComponent } from '../order-history-details/order-history-details.component';
import { RerunRendererComponent } from '../rerun-renderer/rerun-renderer.component';
import { ResetRendererComponent } from '../reset-renderer/reset-renderer.component';
import { OrdersDatasourceService } from '../services/orders-datasource.service';

@UntilDestroy()
@Component({
  selector: 'app-orders-overview',
  standalone: false,
  templateUrl: './orders-overview.component.html',
  styleUrls: ['./orders-overview.component.scss'],
})
export class OrdersOverviewComponent implements OnInit {
  gridApi!: GridApi;
  canResetOrders = false;
  resultsDeliveryStatuses = [
    'NEW',
    'ANALYZING',
    'SEARCH_IN_PROGRESS',
    'SEARCH_COMPLETED',
    'RISK_ASSESSMENT_IN_PROGRESS',
    'RISK_ASSESSMENT_COMPLETED',
    'COMPLETED',
    'SEARCH_FAILED',
    'TOO_MANY_RESULTS',
    'RISK_ASSESSMENT_FAILED',
    'CANCELLATION_REQUESTED',
    'CANCELLED',
    'ERROR',
  ];
  readonly gridOptions: GridOptions = {
    suppressHeaderFocus: true,
    getRowId: (t) => t.data.id,
    getRowHeight: () => 35,
    rowModelType: 'serverSide',
    blockLoadDebounceMillis: 1000,
    enableCellTextSelection: true,
    suppressHorizontalScroll: false,
    suppressMovableColumns: true,
    suppressCellFocus: true,
    suppressRowClickSelection: true,
    detailCellRenderer: OrderHistoryDetailsComponent,
    masterDetail: true,
    detailRowAutoHeight: true,
    overlayLoadingTemplate: '<span></span>',

    context: {
      canResetOrders: false,
    },

    columnDefs: [
      {
        field: '',
        cellRenderer: 'agGroupCellRenderer',
        valueGetter: () => '',
        resizable: true,
        minWidth: 60,
        menuTabs: [],
      },
      {
        headerName: 'Order Id',
        headerTooltip: 'Order Id',
        field: 'orderNo',
        filter: 'textColumnFilterWithoutOptions',
        tooltipComponent: 'materialTooltip',
        tooltipValueGetter: (params) => params.data.orderNo,
        filterParams: FilterParams.textFilterParams,
        menuTabs: ['filterMenuTab'],
        resizable: true,
        minWidth: 120,
      },
      {
        headerName: 'Search Id',
        headerTooltip: 'Search Id',
        field: 'searchId',
        filter: 'textColumnFilterWithoutOptions',
        tooltipComponent: 'materialTooltip',
        tooltipValueGetter: (params) => params.data.searchId,
        filterParams: FilterParams.textFilterParams,
        menuTabs: ['filterMenuTab'],
        resizable: true,
        minWidth: 120,
      },
      {
        headerName: 'Production Order',
        headerTooltip: 'Production Order',
        field: 'productionOrderNo',
        filter: 'textColumnFilterWithoutOptions',
        cellRenderer: (params: any) => {
          return (
            params.data.productionOrderNo +
            ' - ' +
            params.data.productionOrderSequence
          );
        },
        tooltipComponent: 'materialTooltip',
        tooltipValueGetter: (params) => {
          return (
            params.data.productionOrderNo +
            ' - ' +
            params.data.productionOrderSequence
          );
        },
        menuTabs: ['filterMenuTab'],
        filterParams: FilterParams.textFilterParams,
        resizable: true,
        minWidth: 120,
      },
      {
        headerName: 'Result Delivery Id',
        headerTooltip: 'Result Delivery Id',
        field: 'id',
        filter: 'textColumnFilterWithoutOptions',
        tooltipComponent: 'materialTooltip',
        tooltipValueGetter: (params) => params.data.id,
        menuTabs: ['filterMenuTab'],
        filterParams: FilterParams.textFilterParams,
        resizable: true,
        minWidth: 210,
      },
      {
        headerName: 'Member Id',
        headerTooltip: 'Member Id',
        field: 'memberId',
        filter: 'textColumnFilterWithoutOptions',
        tooltipComponent: 'materialTooltip',
        tooltipValueGetter: (params) => params.data.memberId,
        menuTabs: [],
        tooltipField: 'memberId',
        resizable: true,
        minWidth: 120,
      },
      {
        headerName: 'Member Name',
        headerTooltip: 'Member Name',
        field: 'memberName',
        filter: 'textColumnFilterWithoutOptions',
        tooltipComponent: 'materialTooltip',
        tooltipValueGetter: (params) => params.data.memberName,
        menuTabs: [],
        tooltipField: 'memberName',
        resizable: true,
        minWidth: 120,
      },
      {
        headerName: 'Status',
        headerTooltip: 'Status',
        field: 'status',
        suppressSizeToFit: true,
        filter: 'agSetColumnFilter',
        tooltipComponent: 'materialTooltip',
        tooltipValueGetter: (params) => params.data.status,
        menuTabs: ['filterMenuTab'],
        resizable: true,
        filterParams: {
          defaultToNothingSelected: true,
          ...FilterParams.setFilterParams,
          values: this.resultsDeliveryStatuses,
        },
        minWidth: 120,
      },
      {
        headerName: 'Last Run Date',
        headerTooltip: 'Last Run Date',
        field: 'date',
        filter: 'agDateColumnFilter',
        filterParams: FilterParams.dateFilterParams,
        valueGetter: (params: any) => {
          return params.data.lastRunDate;
        },
        tooltipComponent: 'materialTooltip',
        menuTabs: ['filterMenuTab'],
        resizable: true,
        minWidth: 150,
      },
      {
        headerName: 'Trademark Name',
        headerTooltip: 'trademarkName',
        field: 'trademarkName',
        filter: 'textColumnFilterWithoutOptions',
        tooltipComponent: 'materialTooltip',
        tooltipValueGetter: (params) => params.data.candidate,
        menuTabs: ['filterMenuTab'],
        resizable: true,
        minWidth: 230,
      },
      {
        headerName: 'Classes',
        field: 'classes',
        headerTooltip: 'Classes',
        tooltipComponent: 'materialTooltip',
        tooltipValueGetter: (params) => params.data.classes,
        menuTabs: [],
        minWidth: 120,
      },
      {
        headerName: 'Registers',
        headerTooltip: 'Registers',
        field: 'registers',
        tooltipComponent: 'materialTooltip',
        tooltipValueGetter: (params) => params.data.registers,
        menuTabs: [],
        resizable: true,
        minWidth: 250,
      },
      {
        headerName: 'Goods',
        headerTooltip: 'Goods',
        field: 'goods',
        tooltipComponent: 'materialTooltip',
        tooltipValueGetter: (params) => params.data.goods,
        menuTabs: [],
        resizable: true,
        minWidth: 120,
      },
      {
        headerName: 'Turnaround',
        headerTooltip: 'Turnaround',
        field: 'turnaroundTime',
        tooltipComponent: 'materialTooltip',
        tooltipValueGetter: (params) => params.data.turnaroundTime,
        filter: 'agSetColumnFilter',
        menuTabs: ['filterMenuTab'],
        filterParams: {
          defaultToNothingSelected: true,
          suppressSorting: true,
          ...FilterParams.setFilterParams,
          values: ['4 HOUR', 'SDS', 'NEXT DAY', '2 DAY', '3 DAY'],
        },
        resizable: true,
        minWidth: 140,
      },
      {
        headerName: 'Total Time',
        headerTooltip: 'Total Time',
        field: 'totalTime',
        tooltipComponent: 'materialTooltip',
        cellRenderer: (params: any) => {
          return !!params.data.totalTime ? params.data.totalTime + 'ms' : '';
        },
        tooltipValueGetter: (params) => params.data.totalTime,
        menuTabs: [],
        resizable: true,
        minWidth: 120,
      },
      {
        headerName: 'Total Results',
        headerTooltip: 'Total Results',
        field: 'totalResults',
        tooltipComponent: 'materialTooltip',
        tooltipValueGetter: (params) => params.data.totalResults,
        menuTabs: [],
        resizable: true,
        minWidth: 120,
      },
      {
        headerName: 'Re-run',
        headerTooltip: 'Re-run',
        field: 'rerun',
        cellRenderer: 'rerunRendererComponent',
        menuTabs: [],
        pinned: 'right',
        minWidth: 150,
      },
      {
        headerName: 'Reset',
        headerTooltip: 'Reset stuck order status',
        field: 'resetStatus',
        cellRenderer: 'resetRendererComponent',
        menuTabs: [],
        pinned: 'right',
        minWidth: 120,
      },
    ],

    components: {
      materialTooltip: MaterialTooltipComponent,
      textColumnFilterWithoutOptions: CustomTextFilterComponent,
      rerunRendererComponent: RerunRendererComponent,
      resetRendererComponent: ResetRendererComponent,
    },

    onGridReady: (event) => {
      event.api.setGridOption(
        'serverSideDatasource',
        this.ordersDatasourceService.createFor(),
      );

      event.api.sizeColumnsToFit();

      fromEvent(window, 'resize')
        .pipe(untilDestroyed(this), debounceTime(250))
        .subscribe(() => {
          event.api.sizeColumnsToFit();
        });

      this.gridApi = event.api;
    },
  };

  constructor(
    private ordersDatasourceService: OrdersDatasourceService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.userService
      .getCurrentUserObservable()
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        const roles = user?.roles || [];
        this.canResetOrders = roles.includes(AnalystRoleEnum.DEV_SUPPORT_ADMIN);
        this.gridOptions.context = {
          canResetOrders: this.canResetOrders,
        };
        if (this.gridApi) {
          this.gridApi.refreshCells({ force: true, columns: ['resetStatus'] });
        }
      });
  }
}
